import { robotsMeta, buildTitle } from "@/lib/seo-quality";
import { assessGlossary } from "@/lib/seo-families/glossary";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { PageHeader } from "./fordon";
import { getGlossaryTerm, type GlossaryTerm } from "@/data/husbil-glossary";
import { getGlossaryDjup } from "@/data/husbil-glossary-djup";

export const Route = createFileRoute("/ordlista/$term")({
  // Loader + component i samma chunk: ordlistedatan (som bara behövs för denna
  // rutt) hamnar då utanför initial-bundlen.
  codeSplitGroupings: [["loader", "component"], ["notFoundComponent"]],
  loader: ({ params }) => {
    const term = getGlossaryTerm(params.term);
    if (!term) throw notFound();
    const djup = getGlossaryDjup(term.slug) ?? null;
    const related: GlossaryTerm[] = term.related
      .map((s: string) => getGlossaryTerm(s))
      .filter((x: GlossaryTerm | undefined): x is GlossaryTerm => Boolean(x))
      .map((r) => r);
    const url = `https://www.akhusbilfritid.se/ordlista/${encodeURIComponent(term.slug)}`;
    const title = buildTitle(`${term.term} — vad betyder det?`, "Husbilsordlista");
    const desc = term.short.length > 155 ? term.short.slice(0, 152) + "..." : term.short;
    // SEO-nyttolasten räknas ut här (loader) så head() inte behöver datan.
    const seo = {
      url,
      title,
      desc,
      robots: robotsMeta(assessGlossary(term)),
      jsonld: [
        {
          "@context": "https://schema.org",
          "@type": "DefinedTerm",
          name: term.term,
          description: term.short,
          inDefinedTermSet: {
            "@type": "DefinedTermSet",
            name: "Husbils\u00adordlista",
            url: "https://www.akhusbilfritid.se/ordlista",
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: `Vad betyder ${term.term}?`,
              acceptedAnswer: { "@type": "Answer", text: term.short },
            },
            ...(djup?.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })) ?? []),
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se" },
            { "@type": "ListItem", position: 2, name: "Ordlista", item: "https://www.akhusbilfritid.se/ordlista" },
            { "@type": "ListItem", position: 3, name: term.term, item: url },
          ],
        },
      ],
    };
    return { term, djup, related, seo };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ name: "robots", content: "noindex, follow" }] };
    const { seo } = loaderData;
    return {
      meta: [
        ...seo.robots,
        { title: seo.title },
        { name: "description", content: seo.desc },
        { property: "og:title", content: seo.title },
        { property: "og:description", content: seo.desc },
        { property: "og:url", content: seo.url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: seo.url }],
      scripts: seo.jsonld.map((j) => ({
        type: "application/ld+json",
        children: JSON.stringify(j),
      })),
    };
  },
  notFoundComponent: OrdlistaNotFound,
  component: OrdlistaTerm,
});

function OrdlistaNotFound() {
  return (
    <div className="max-w-2xl mx-auto p-8 text-center">
      <h1 className="text-2xl font-bold mb-4">Termen finns inte i ordlistan</h1>
      <Link to="/ordlista" className="text-primary underline">Se alla termer</Link>
    </div>
  );
}

function OrdlistaTerm() {
  const { term, djup, related } = Route.useLoaderData();
  return (
    <>
      <PageHeader label={`Ordlista · ${term.category}`} title={term.term} sub="Definition & förklaring" />
      <main className="max-w-3xl mx-auto px-4 py-10 space-y-10">
        <nav className="text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-2">/</span>
          <Link to="/ordlista" className="hover:underline">Ordlista</Link>
          <span className="mx-2">/</span>
          <span>{term.term}</span>
        </nav>

        <section className="rounded-xl border-2 border-primary/20 bg-muted/40 p-6">
          <div className="text-xs uppercase text-muted-foreground mb-1">Kort definition</div>
          <p className="text-lg leading-relaxed">{term.short}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3">Mer om {term.term}</h2>
          <p className="text-base leading-relaxed">{term.long}</p>
        </section>

        {djup && (
          <section>
            <h2 className="text-2xl font-bold mb-3">Vad betyder det i praktiken?</h2>
            <p className="text-base leading-relaxed mb-6">{djup.praktiskt}</p>
            <dl className="grid sm:grid-cols-2 gap-3 mb-6">
              {djup.siffror.map((s) => (
                <div key={s.label} className="rounded-lg border bg-muted/30 p-4">
                  <dt className="text-xs uppercase text-muted-foreground">{s.label}</dt>
                  <dd className="text-lg font-semibold">{s.value}</dd>
                </div>
              ))}
            </dl>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-lg border p-4">
                <h3 className="font-semibold mb-2">Vanligt misstag</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{djup.misstag}</p>
              </div>
              <div className="rounded-lg border p-4">
                <h3 className="font-semibold mb-2">Vårt tips som handlare i Norrbotten</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{djup.vartTips}</p>
              </div>
            </div>
          </section>
        )}

        {djup && djup.faq.length > 0 && (
          <section>
            <h2 className="text-xl font-bold mb-3">Vanliga frågor</h2>
            <div className="space-y-4">
              {djup.faq.map((f) => (
                <div key={f.q} className="rounded-lg border p-4">
                  <div className="font-semibold mb-1">{f.q}</div>
                  <p className="text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section>
            <h2 className="text-xl font-bold mb-3">Relaterade termer</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/ordlista/$term"
                  params={{ term: r.slug }}
                  className="rounded-lg border p-4 hover:border-primary hover:bg-muted/40"
                >
                  <div className="text-xs text-muted-foreground">{r.category}</div>
                  <div className="font-semibold">{r.term} →</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <AdSlot variant="in-article" />


        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-xl font-bold mb-3">Fråga oss direkt istället</h2>
          <p className="mb-6 opacity-90">Vi har 20+ års erfarenhet av husbil. Ingen fråga är dum.</p>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
            <Phone className="w-4 h-4" /> 070-862 97 43
          </a>
        </section>
      </main>
    </>
  );
}
